import React, { useState } from 'react';
import Head from 'next/head';
import { Box, Grid } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HistoryIcon from '@mui/icons-material/History';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import InfoIcon from '@mui/icons-material/Info';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';

import {
  PermissionsGate,
  GreenCardForm,
  UploadGreenCardDocument,
  TabsV2,
  CommentsV2,
  ActivityListV2,
  TaskCarDetails,
  TaskDetailScrollableSection,
} from '@components';

import { DrawerLayout, TaskLayout } from '@layouts';

import { useGreenCardTaskOperations } from '@hooks';
import { SCOPES } from '@constants';
import { asyncForEach, showError } from '@utils';
import { boardsServices } from '@services';

import { UploadDocumentV3 } from 'components/common/media';
import { ButtonV2 } from 'components/common/ui';
import {
  GREEN_CARD_TASK_FIELD_NAMES,
  GREEN_CARD_TASK_STATUSES_NAMES,
} from 'components/greenCard/constants';
import { TaskSection } from 'components/common/TaskSection';
import { MoreButton } from 'components/common/MoreButton';
import { getGreenCardTaskPayload } from 'hooks/greenCard/utils';

export const tabs = [
  {
    label: 'Ticket Details',
    id: 'ticket-details',
    icon: <InfoIcon data-testid="InfoIcon" />,
  },
  {
    label: 'Affirmation Progress',
    id: 'affirmation-progress',
    icon: <ChecklistIcon data-testid="ChecklistIcon" />,
  },
  {
    label: 'Development Progress',
    id: 'development-progress',
    icon: <DeveloperBoardIcon data-testid="DeveloperBoardIcon" />,
  },
  {
    label: 'Comments',
    id: 'comments',
    icon: <ChatBubbleIcon data-testid="ChatBubbleIcon" />,
  },
  {
    label: 'History',
    id: 'history',
    icon: <HistoryIcon data-testid="HistoryIcon" />,
  },
];

export const rightTabs = [
  {
    icon: <DirectionsCarIcon data-testid="DirectionsCarIcon" />,
    id: 'car-details',
    label: 'Car Details',
  },
  {
    icon: <InsertDriveFileIcon data-testid="InsertDriveFileIcon" />,
    id: 'documents',
    label: 'Documents',
  },
];

const GreenCardTask = () => {
  const {
    task,
    vehicle,
    taskId,
    assignees,
    commentsUsers,
    loading,
    getGreenCardTask,
  } = useGreenCardTaskOperations();
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);

  const handleChangeStatus = async (status) => {
    try {
      await boardsServices.updateTask(taskId, {
        id: taskId,
        status,
      });

      await getGreenCardTask();
    } catch (error) {
      showError(error);
    }
  };

  const renderHeaderOptions = () => {
    return (
      <PermissionsGate>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <MoreButton
            moreOptions={[
              {
                title: 'Delivered',
                onClick: () => handleChangeStatus(26),
                scopes: [SCOPES.canEdit],
                hidden: task?.status !== 25,
              },
              {
                title: 'Returned',
                onClick: () => handleChangeStatus(3),
                scopes: [SCOPES.canEdit],
                hidden: task?.status !== 26,
              },
              {
                title: 'Cancel Ticket',

                onClick: () => handleChangeStatus(12),
                scopes: [SCOPES.canEdit],
                hidden: task?.status?.value === 12,
              },
            ]}
          />
          <ButtonV2
            type="submit"
            form="green-card-form"
            disabled={loading || submitting}
          >
            Save
          </ButtonV2>
        </Box>
      </PermissionsGate>
    );
  };

  const handleAddComment = async (comment) => {
    try {
      setSubmitting(true);
      await boardsServices.addTaskComment(taskId, comment);
      await getGreenCardTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, comment) => {
    try {
      setSubmitting(true);
      await boardsServices.editTaskComment(taskId, commentId, comment);
      await getGreenCardTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setSubmitting(true);
      await boardsServices.deleteTaskComment(taskId, commentId);
      await getGreenCardTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadDocument = async (form) => {
    try {
      setSubmitting(true);
      await asyncForEach(form?.documents, async (document) => {
        await boardsServices.uploadTaskDocument(taskId, document, form?.type);
      });
      await getGreenCardTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      setSubmitting(true);
      await boardsServices.deleteTaskDocument(taskId, docId);
      await getGreenCardTask();
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (form) => {
    const documentsToUpLoad = [...documents]?.filter((doc) => doc?.localFile);

    try {
      setSubmitting(true);
      const values = getGreenCardTaskPayload(form);

      await boardsServices.updateTask(taskId, {
        id: taskId,
        ...values,
      });

      await asyncForEach(documentsToUpLoad, async (doc) => {
        await boardsServices.uploadTaskDocument(
          taskId,
          doc?.file,
          '',
          doc?.tags
        );
      });

      await getGreenCardTask();
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const taskStatusNames = Object.keys(GREEN_CARD_TASK_STATUSES_NAMES).reduce(
    (acc, cur) => {
      acc[cur] = GREEN_CARD_TASK_STATUSES_NAMES[cur].label;

      return acc;
    },
    {}
  );

  return (
    <>
      <Head>
        <title>{`Green Card: ${taskId} `}</title>
        <meta name="description" content="Instacar instafleet" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        isLoading={loading || submitting}
        task={taskId}
        renderHeaderOptions={renderHeaderOptions}
        links={[
          {
            title: 'Green Card',
            href: '/greenCard',
          },
          { title: taskId },
          { title: GREEN_CARD_TASK_STATUSES_NAMES[task?.status]?.label },
        ]}
        scopes={[SCOPES.canView]}
      >
        <Grid container alignItems="start" spacing={1}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TabsV2 tabs={tabs} observerRootId="greenCard-task-details" />

            <TaskDetailScrollableSection id="greenCard-task-details">
              <GreenCardForm
                onSubmit={handleSubmit}
                defaultValues={task}
                loading={loading || submitting}
                assignees={assignees}
              />
              <TaskSection
                title={tabs[3].label}
                id={tabs[3].id}
                icon={tabs[3].icon}
              >
                <CommentsV2
                  comments={task?.comments}
                  onSubmit={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  users={commentsUsers || []}
                />
              </TaskSection>
              <TaskSection
                title={tabs[4].label}
                id={tabs[4].id}
                icon={tabs[4].icon}
              >
                <ActivityListV2
                  activities={task?.changelog}
                  taskFieldNames={GREEN_CARD_TASK_FIELD_NAMES}
                  taskStatusNames={taskStatusNames}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TabsV2 tabs={rightTabs} observerRootId="greenCard-task-right" />

            <TaskDetailScrollableSection id="greenCard-task-right">
              <TaskSection
                isSmall
                title={rightTabs[0].label}
                id={rightTabs[0].id}
                icon={rightTabs[0].icon}
              >
                <TaskCarDetails vehicle={vehicle} />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[1].label}
                id={rightTabs[1].id}
                icon={rightTabs[1].icon}
              >
                <UploadDocumentV3
                  enableUpload={false}
                  documents={task?.documents}
                  onChange={setDocuments}
                  onDeleteDocument={handleDeleteDocument}
                  loading={loading || submitting}
                  UploadComponent={
                    <UploadGreenCardDocument
                      onSubmit={handleUploadDocument}
                      isLoading={loading || submitting}
                    />
                  }
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
      </TaskLayout>
    </>
  );
};

GreenCardTask.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default GreenCardTask;
